import { useState } from 'react';
import { BillType, PixKeyType } from '../../enums';
import { formatAsRegionalPhoneCode } from '../../utils/pixFormatters';
import { toBatchPayableGenericPixKeys } from '../../utils/pixKeyUtils';
import { useValidatePixKeysBatch } from '../useValidatePixKeysBatch';
function formatKeyToValidate(keyType, key) {
    return keyType === PixKeyType.PHONE ? formatAsRegionalPhoneCode(key) : key;
}
export function useGetAccountsPayablePixKeys() {
    const { validatePixKeysBatch, loading } = useValidatePixKeysBatch();
    const [accountsPayablePixKeys, setAccountsPayablePixKeys] = useState();
    const [errors, setErrors] = useState();
    function enrich(accountsPayable) {
        return toBatchPayableGenericPixKeys(accountsPayable).flatMap(pixKey => {
            const accountPayable = accountsPayable.find(value => value.id === pixKey.accountPayablesId);
            if (!accountPayable) {
                return [];
            }
            return [{ ...accountPayable, pixKey }];
        });
    }
    async function getAccountsPayablePixKeys(accountsPayable) {
        setAccountsPayablePixKeys(undefined);
        setErrors(undefined);
        const keysToCheck = accountsPayable.flatMap(value => {
            const paymentDetails = value.employeePaymentDetailsInfo?.paymentDetails;
            if (paymentDetails?.type !== BillType.PIX) {
                return [];
            }
            const { keyType, key } = paymentDetails.details;
            if (!keyType || keyType === PixKeyType.BANK_ACCOUNT || !key) {
                return [];
            }
            return [formatKeyToValidate(keyType, key)];
        });
        if (keysToCheck.length === 0) {
            setAccountsPayablePixKeys(enrich(accountsPayable));
            return;
        }
        const batchResult = await validatePixKeysBatch(keysToCheck);
        if (!batchResult) {
            return;
        }
        if (batchResult.invalid.length > 0) {
            const invalidSet = new Set(batchResult.invalid);
            const invalidAccountsPayable = accountsPayable.filter(value => {
                const paymentDetails = value.employeePaymentDetailsInfo?.paymentDetails;
                if (paymentDetails?.type !== BillType.PIX) {
                    return false;
                }
                const { keyType, key } = paymentDetails.details;
                return !!keyType && !!key && invalidSet.has(formatKeyToValidate(keyType, key));
            });
            const validAccountsPayable = accountsPayable.filter(value => !invalidAccountsPayable.includes(value));
            setErrors(invalidAccountsPayable.map(value => ({ value, error: new Error('invalid-pix-key') })));
            if (validAccountsPayable.length > 0) {
                setAccountsPayablePixKeys(enrich(validAccountsPayable));
            }
            return;
        }
        setAccountsPayablePixKeys(enrich(accountsPayable));
    }
    return {
        accountsPayablePixKeys,
        setAccountsPayablePixKeys,
        errors,
        setErrors,
        loading,
        getAccountsPayablePixKeys,
    };
}
